// src/pages/nfl/NflPosProjections.jsx

import React, { useEffect, useMemo, useState } from "react";

/* ---------------- data sources ---------------- */

const PLAYER_SIMULATIONS_URL = "/data/nfl/classic/latest/playersimulations.json";

const POS_LIST = ["QB", "RB", "WR", "TE", "DST"];

const NAME_XWALK_URL = "/data/nfl/classic/latest/name_xwalk.json";

const PROJECTIONS_URL = "/data/nfl/classic/latest/projections.json";

const VEGAS_URL = "/data/nfl/classic/latest/vegas.json";

const SITE_IDS_URL = "/data/nfl/classic/latest/site_ids.json";

const SLATES = [{ id: "main", label: "Main" }, { id: "early", label: "Early" }, { id: "late", label: "Late" }, { id: "thu_mon", label: "Thu–Mon" }];

const META_URL = "/data/nfl/classic/latest/meta.json";

const gameKey = (row) => row.team && row.opp ? [row.team, row.opp].sort().join("@") : "";

const teamLogo = (abbr) => (abbr ? `/logos/nfl/${abbr}.png` : "");

/* ---------------- helpers ---------------- */

const num = (v) => {

  if (v == null || String(v).trim() === "") return null;

  const n = Number(String(v ?? "").replace(/[,%\s]/g, ""));

  return Number.isFinite(n) ? n : null;

};

const pct = (v) => {

  if (v == null || v === "") return "";

  const s = String(v).trim();

  if (s.endsWith("%")) return s;

  const n = num(s);

  if (n == null) return "";

  const val =

    Math.abs(n) <= 1 ? (n * 100).toFixed(1) : n.toFixed(1);

  return `${val}%`;

};

const smart1 = (v) => {

  const n = num(v);

  return n == null ? "" : Number.isInteger(n) ? String(n) : n.toFixed(1);

};

const int0 = (v) => {

  const n = num(v);

  return n == null ? "" : Math.round(n).toLocaleString();

};

const normalizeName = (s) =>

  String(s || "")

    .toLowerCase()

    .normalize("NFKD")

    .replace(/[\u0300-\u036f]/g, "")

    .replace(/[.'’,-]/g, " ")

    .replace(/\s+(jr|sr|ii|iii|iv)\b/g, "")

    .replace(/\s+/g, " ")

    .trim();

/* canonicalize header keys */

const canonKey = (s = "") =>

  String(s)

    .replace(/\u00A0|\u202F/g, " ")

    .toLowerCase()

    .replace(/\s+/g, " ")

    .trim()

    .replace(/[%]/g, "")

    .replace(/_/g, " ")

    .replace(/[.]/g, "")

    .replace(/\s+/g, "");

const buildKeyMap = (row) => {

  const m = {};

  for (const [k, v] of Object.entries(row)) m[canonKey(k)] = v;

  return m;

};

const getVal = (kmap, ...cands) => {

  for (const c of cands) {

    const v = kmap[canonKey(c)];

    if (v !== undefined) return v;

  }

  return "";

};

async function fetchJson(url) {

  const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });

  if (!r.ok) throw new Error(`HTTP ${r.status}`);

  const j = await r.json();

  if (j && (Array.isArray(j.dk) || Array.isArray(j.fd))) {

    return ["dk", "fd"].flatMap((site) => (j[site] || []).map((row) => ({ ...row, site })));

  }

  return Array.isArray(j) ? j : j?.rows ?? [];

}

function useJson(url) {

  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(true);

  const [err, setErr] = useState("");

  useEffect(() => {

    let ok = true;

    (async () => {

      setLoading(true);

      setErr("");

      try {

        const data = await fetchJson(url);

        if (ok) setRows(data);

      } catch (e) {

        if (ok) setErr(String(e));

      } finally {

        if (ok) setLoading(false);

      }

    })();

    return () => {

      ok = false;

    };

  }, [url]);

  return { rows, loading, err };

}

/* meta fetch (for last-updated tag) */

function useMeta(url) {

  const [meta, setMeta] = useState(null);

  useEffect(() => {

    let ok = true;

    (async () => {

      try {

        const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });

        if (!r.ok) throw new Error();

        const j = await r.json();

        if (ok) setMeta(j);

      } catch {

        if (ok) setMeta(null);

      }

    })();

    return () => {

      ok = false;

    };

  }, [url]);

  return meta;

}

/* ---------------- column sets ---------------- */

const COLS_COMMON = [

  { id: "player", label: "Player", type: "text", w: "min-w-[12rem] text-left" },

  { id: "team", label: "Team", type: "team", w: "min-w-[4.5rem]" },

  { id: "opp", label: "Opp", type: "team", w: "min-w-[4.5rem]" },

  { id: "dk_sal", label: "DK Sal", type: "money", w: "min-w-[4.25rem]" },

  { id: "fd_sal", label: "FD Sal", type: "money", w: "min-w-[4.25rem]" },

];

const COLS_QB = [

  ...COLS_COMMON,

  { id: "pa_yards", label: "Pa Yards", type: "num1" },

  { id: "pa_att", label: "Pa Att", type: "num1" },

  { id: "pa_comp", label: "Pa Comp", type: "num1" },

  { id: "pa_pct", label: "Comp%", type: "pct" },

  { id: "pa_td", label: "Pa TD", type: "num1" },

  { id: "int", label: "INT", type: "num1" },

  { id: "ru_att", label: "Ru Att", type: "num1" },

  { id: "ypc", label: "YPC", type: "num1" },

  { id: "ru_yds", label: "Ru Yds", type: "num1" },

  { id: "ru_td", label: "Ru TD", type: "num1" },

  { id: "dk_proj", label: "DK Proj", type: "num1" },

  { id: "dk_val", label: "DK Val", type: "num1" },

  { id: "dk_pown", label: "DK pOWN%", type: "pct" },

  { id: "fd_proj", label: "FD Proj", type: "num1" },

  { id: "fd_val", label: "FD Val", type: "num1" },

  { id: "fd_pown", label: "FD pOWN%", type: "pct" },

];

const COLS_RB = [

  ...COLS_COMMON,

  { id: "ru_att", label: "Ru Attempts", type: "num1" },

  { id: "ypc", label: "YPC", type: "num1" },

  { id: "ru_yds", label: "Ru Yards", type: "num1" },

  { id: "ru_td", label: "Ru TD", type: "num1" },

  { id: "targets", label: "Targets", type: "num1" },

  { id: "tgt_share", label: "Tgt Share", type: "pct" },

  { id: "rec", label: "Rec", type: "num1" },

  { id: "rec_yds", label: "Rec Yards", type: "num1" },

  { id: "rec_td", label: "Rec TD", type: "num1" },

  { id: "dk_proj", label: "DK Proj", type: "num1" },

  { id: "dk_val", label: "DK Val", type: "num1" },

  { id: "dk_pown", label: "DK pOWN%", type: "pct" },

  { id: "fd_proj", label: "FD Proj", type: "num1" },

  { id: "fd_val", label: "FD Val", type: "num1" },

  { id: "fd_pown", label: "FD pOWN%", type: "pct" },

];

const COLS_TE = [

  ...COLS_COMMON,

  { id: "targets", label: "Targets", type: "num1" },

  { id: "tgt_share", label: "Tgt Share", type: "pct" },

  { id: "rec", label: "Rec", type: "num1" },

  { id: "rec_yds", label: "Rec Yards", type: "num1" },

  { id: "rec_td", label: "Rec TD", type: "num1" },

  { id: "dk_proj", label: "DK Proj", type: "num1" },

  { id: "dk_val", label: "DK Val", type: "num1" },

  { id: "dk_pown", label: "DK pOWN%", type: "pct" },

  { id: "fd_proj", label: "FD Proj", type: "num1" },

  { id: "fd_val", label: "FD Val", type: "num1" },

  { id: "fd_pown", label: "FD pOWN%", type: "pct" },

];

const COLS_WR = COLS_RB;

const COLS_DST = [

  { id: "player", label: "DST", type: "text", w: "min-w-[12rem] text-left" },

  { id: "team", label: "Team", type: "team", w: "min-w-[4.5rem]" },

  { id: "opp", label: "Opp", type: "team", w: "min-w-[4.5rem]" },

  { id: "dk_sal", label: "DK Sal", type: "money", w: "min-w-[4.25rem]" },

  { id: "fd_sal", label: "FD Sal", type: "money", w: "min-w-[4.25rem]" },

  { id: "sacks", label: "Sacks", type: "num1" },

  { id: "int", label: "Int", type: "num1" },

  { id: "fumbles", label: "Fumbles", type: "num1" },

  { id: "def_td", label: "DefTD", type: "num1" },

  { id: "points", label: "Points", type: "num1" },

  { id: "dk_proj", label: "DK Proj", type: "num1" },

  { id: "dk_val", label: "DK Val", type: "num1" },

  { id: "dk_pown", label: "DK pOWN%", type: "pct" },

  { id: "fd_proj", label: "FD Proj", type: "num1" },

  { id: "fd_val", label: "FD Val", type: "num1" },

  { id: "fd_pown", label: "FD pOWN%", type: "pct" },

];

const POS_TO_COLS = {

  QB: COLS_QB,

  RB: COLS_RB,

  WR: COLS_WR,

  TE: COLS_TE,

  DST: COLS_DST,

};

/* ---------------- CSV ---------------- */

const escapeCSV = (s) =>

  /[",\r\n]/.test(String(s ?? ""))

    ? `"${String(s ?? "").replace(/"/g, '""')}"`

    : String(s ?? "");

const formatVal = (type, raw) =>

  type === "money"

    ? int0(raw)

    : type === "pct"

    ? pct(raw)

    : type === "num1"

    ? smart1(raw)

    : raw ?? "";

function downloadCSV(rows, cols, fname) {

  const header = cols.map((c) => c.label).join(",");

  const body = rows

    .map((r) =>

      cols.map((c) => escapeCSV(formatVal(c.type, r[c.id]))).join(",")

    )

    .join("\n");

  const blob = new Blob([header + "\n" + body], {

    type: "text/csv;charset=utf-8;",

  });

  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");

  a.href = url;

  a.download = fname;

  a.click();

  URL.revokeObjectURL(url);

}

/* ---------------- heatmap palette ---------------- */

function dirFor(colId, activePos) {

  if (!colId) return null;

  const id = String(colId).toLowerCase();

  if (id.includes("sal")) return null;

  if (id === "team" || id === "opp" || id === "player") return null;

  // QB: INT lower is better; DST: INT higher is better

  if (id === "int") return activePos === "QB" ? "lower" : "higher";

  if (/pown$/.test(id)) return "lower";

  if (/(_proj|_val)$/.test(id)) return "higher";

  return "higher";

}

function heatTextColor(min, max, v, dir, theme = "dark") {

  if (v == null || min == null || max == null || min === max || !dir) return null;

  let t = (v - min) / (max - min);

  t = Math.max(0, Math.min(1, t));

  if (dir === "lower") t = 1 - t;

  const isDark = theme === "dark";

  let hue;

  let sat;

  let light;

  if (isDark) {

    if (t < 0.5) {

      const u = t / 0.5; // red -> yellow

      hue = 0 + u * 60;

      sat = 80;

      light = 62 + u * 10;

    } else {

      const u = (t - 0.5) / 0.5; // yellow -> green

      hue = 60 + u * 60;

      sat = 75;

      light = 72 - u * 8;

    }

  } else {

    if (t < 0.5) {

      const u = t / 0.5;

      hue = 0 + u * 60;

      sat = 85;

      light = 42 + u * 6;

    } else {

      const u = (t - 0.5) / 0.5;

      hue = 60 + u * 60;

      sat = 80;

      light = 38 - u * 4;

    }

  }

  return `hsl(${hue}, ${sat}%, ${light}%)`;

}

/* ---------------- component ---------------- */

export default function NflPosProjections({ pos }) {

  // default pos: from prop → URL ?pos= → QB

  const initialPos = useMemo(() => {

    const qp = new URLSearchParams(window.location.search);

    const fromQuery = qp.get("pos");

    const p = String(pos || fromQuery || "QB").toUpperCase();

    return POS_LIST.includes(p) ? p : "QB";

  }, [pos]);

  const [activePos, setActivePos] = useState(initialPos);

  // keep URL in sync (so refresh keeps the same position)

  useEffect(() => {

    const qp = new URLSearchParams(window.location.search);

    qp.set("pos", activePos);

    const next = `${window.location.pathname}?${qp.toString()}`;

    window.history.replaceState({}, "", next);

  }, [activePos]);

  const baseCols = POS_TO_COLS[activePos] || POS_TO_COLS.QB;

  const { rows: simulationRows, loading: simsLoading, err: simsErr } = useJson(PLAYER_SIMULATIONS_URL);

  const { rows: projectionRows, loading: projectionsLoading, err: projectionsErr } = useJson(PROJECTIONS_URL);

  const { rows: siteRows, loading: sitesLoading, err: sitesErr } = useJson(SITE_IDS_URL);

  const { rows: vegasRows, loading: vegasLoading, err: vegasErr } = useJson(VEGAS_URL);

  const [slate, setSlate] = useState(() => {

    const value = new URLSearchParams(window.location.search).get("slate");

    return SLATES.some((item) => item.id === value) ? value : "main";

  });

  const [slateSite, setSlateSite] = useState(() => {

    const value = new URLSearchParams(window.location.search).get("site");

    return ["both", "dk", "fd"].includes(value) ? value : "both";

  });

  useEffect(() => {

    const qp = new URLSearchParams(window.location.search);

    qp.set("slate", slate);

    qp.set("site", slateSite);

    window.history.replaceState({}, "", `${window.location.pathname}?${qp}`);

  }, [slate, slateSite]);

  const loading = simsLoading || projectionsLoading || sitesLoading || vegasLoading;

  const err = simsErr || projectionsErr || sitesErr || vegasErr;

  // Match supplemental data by name + team + position. DST names can differ

  // between sources, so defenses also match by team + position.

  const rawRows = useMemo(() => {

    const byPlayer = new Map();

    const byDefense = new Map();

    const canonicalTeam = (value) => {

      const team = String(value || "").trim().toUpperCase();

      return team === "JAC" ? "JAX" : team;

    };

    const position = (value) => {

      const pos = String(value || "").trim().toUpperCase().split("/")[0].replace(/\d+$/, "");

      return ["D", "DEF"].includes(pos) ? "DST" : pos;

    };

    for (const row of projectionRows) {

      const k = buildKeyMap(row);

      const player = getVal(k, "player", "player name", "name", "dst");

      const team = canonicalTeam(getVal(k, "team", "teamabbrev"));

      const pos = position(getVal(k, "pos", "position", "slot"));

      const extra = {

        opp: getVal(k, "opp", "opponent"),

        dk_sal: getVal(k, "dk_sal", "dk sal"),

        fd_sal: getVal(k, "fd_sal", "fd sal"),

        dk_pown: getVal(k, "dk_pown", "dk pown%", "dk pown"),

        fd_pown: getVal(k, "fd_pown", "fd pown%", "fd pown"),

      };

      byPlayer.set(`${normalizeName(player)}|${team}|${pos}`, extra);

      if (pos === "DST") byDefense.set(`${team}|DST`, extra);

    }

    const idKey = slate === "main" ? "id" : `${slate}_id`;

    const salaryKey = slate === "main" ? "salary" : `${slate}_salary`;

    const sitesByKey = new Map();

    const sitesByName = new Map();

    const defenses = new Map();

    const nameTeams = new Map();

    for (const entry of siteRows) {

      const team = canonicalTeam(entry.team);

      const pos = position(entry.pos);

      const name = normalizeName(entry.name);

      const fullKey = `${entry.site}|${name}|${team}|${pos}`;

      sitesByKey.set(fullKey, entry);

      const nameKey = `${entry.site}|${name}|${pos}`;

      const candidates = sitesByName.get(nameKey) || [];

      candidates.push(entry);

      sitesByName.set(nameKey, candidates);

      if (pos === "DST") defenses.set(`${entry.site}|${team}`, entry);

      const teams = nameTeams.get(`${name}|${pos}`) || new Set();

      if (team) teams.add(team);

      nameTeams.set(`${name}|${pos}`, teams);

    }

    const vegasOpp = new Map(vegasRows.map((row) => [canonicalTeam(row.team), canonicalTeam(row.opponent)]));

    const eligible = (entry) => Boolean(entry && String(entry[idKey] || "").trim()

      && num(entry[salaryKey]) > 0 && num(entry[salaryKey]) < 50000);

    return simulationRows

      .filter((row) => position(row.pos) === activePos)

      .map((row) => {

        const pos = position(row.pos);

        const name = normalizeName(row.player);

        let team = canonicalTeam(row.team);

        if (!team) {

          const candidates = nameTeams.get(`${name}|${pos}`);

          if (candidates?.size === 1) team = [...candidates][0];

        }

        const extra = byPlayer.get(`${name}|${team}|${pos}`)

          || (pos === "DST" ? byDefense.get(`${team}|DST`) : null) || {};

        const merged = { ...extra, ...row, team, pos };

        merged.opp = vegasOpp.get(team) || merged.opp || "";

        let included = false;

        for (const site of ["dk", "fd"]) {

          let entry = sitesByKey.get(`${site}|${name}|${team}|${pos}`);

          if (!entry && pos === "DST") entry = defenses.get(`${site}|${team}`);

          if (!entry && !team) {

            const candidates = sitesByName.get(`${site}|${name}|${pos}`);

            if (candidates?.length === 1) entry = candidates[0];

          }

          const available = eligible(entry);

          if (available && (slateSite === "both" || slateSite === site)) included = true;

          // Never borrow another slate's salary when this player is unavailable.

          merged[`${site}_sal`] = available ? num(entry[salaryKey]) : null;

          merged[`${site}_id`] = available ? String(entry[idKey]) : null;

          const salary = merged[`${site}_sal`];

          const projection = num(merged[`${site}_proj`]);

          merged[`${site}_val`] = salary > 0 && projection != null

            ? projection / (salary / 1000) : null;

        }

        return included ? merged : null;

      }).filter(Boolean);

  }, [simulationRows, projectionRows, siteRows, vegasRows, activePos, slate, slateSite]);

  const meta = useMeta(META_URL);

  // theme

  const [theme, setTheme] = useState("dark");

  const isDark = theme === "dark";

  // name→team/opponent backfill (xwalk + all-projections)

  const [nameToTeam, setNameToTeam] = useState({});

  const [nameToOpp, setNameToOpp] = useState({});

  const [teamToOpp, setTeamToOpp] = useState({});

  useEffect(() => {

    (async () => {

      try {

        const xw = await fetchJson(NAME_XWALK_URL).catch(() => []);

        const all = projectionRows;

        const teamMap = {};

        const oppMap = {};

        const teamOppMap = {};

        for (const r of xw) {

          const n = r.player ?? r["Player Name"];

          const t = r.team ?? r["Team"];

          if (n && t) teamMap[normalizeName(n)] = String(t).toUpperCase();

        }

        for (const r of all) {

          const k = buildKeyMap(r);

          const n = r.player ?? r["Player Name"] ?? getVal(k, "player", "player name", "name");

          const t = r.team ?? r["Team"] ?? getVal(k, "team", "teamabbrev", "team abbrev");

          const o = getVal(

            k,

            "opp",

            "opponent",

            "opp team",

            "opponent team",

            "opp_team",

            "opponent_team"

          );

          const nameKey = n ? normalizeName(n) : "";

          const team = t ? String(t).trim().toUpperCase() : "";

          const opp = o ? String(o).trim().toUpperCase().replace(/^@/, "") : "";

          if (nameKey && team && !teamMap[nameKey]) teamMap[nameKey] = team;

          if (nameKey && opp) oppMap[nameKey] = opp;

          if (team && opp) teamOppMap[team] = opp;

        }

        setNameToTeam(teamMap);

        setNameToOpp(oppMap);

        setTeamToOpp(teamOppMap);

      } catch {

        setNameToTeam({});

        setNameToOpp({});

        setTeamToOpp({});

      }

    })();

  }, [projectionRows]);

  // normalize each row to a stable shape

  const rows = useMemo(

    () =>

      rawRows.map((row) => {

        const k = buildKeyMap(row);

        const player = (

          getVal(

            k,

            "player",

            "player name",

            "name",

            "dst",

            "defense",

            "qb",

            "rb",

            "rb1",

            "wr",

            "wr1",

            "te",

            "te1"

          ) || ""

        )

          .toString()

          .trim();

        let team = (getVal(k, "team", "teamabbrev") || "")

          .toString()

          .trim()

          .toUpperCase();

        if (!team && player) {

          const t = nameToTeam[normalizeName(player)];

          if (t) team = t;

        }

        let opp = getVal(

          k,

          "opp",

          "opponent",

          "opp team",

          "opponent team",

          "opp_team",

          "opponent_team"

        );

        opp = String(opp || "").trim().toUpperCase().replace(/^@/, "");

        if (!opp && player) {

          const o = nameToOpp[normalizeName(player)];

          if (o) opp = o;

        }

        if (!opp && team) {

          const o = teamToOpp[team];

          if (o) opp = o;

        }

        const dk_sal = getVal(k, "dk sal", "dk_sal", "dk\u00a0sal");

        const fd_sal = getVal(k, "fd sal", "fd_sal", "fd\u00a0sal");

        // QB

        const pa_yards = getVal(k, "pa yards", "pass yards", ".toggle", "pa_yards");

        const pa_att = getVal(k, "pa att", "pass attempts", "pa attempts", "pa_att");

        const pa_comp = getVal(k, "pa comp", "pass comp", "pa_comp");

        const pa_pct = getVal(

          k,

          "pa_comp_pct",

          "comp%",

          "completion%",

          "pass comp%",

          "pa comp%"

        );

        const pa_td = getVal(k, "pa td", "pa_td");

        const intVal = getVal(k, "int", "INT");

        // RB/WR rushing + receiving

        const ru_att = getVal(

          k,

          "ru_att",

          "ru attempts",

          "ru\u00A0attempts",

          "ru att",

          "rush attempts",

          "rush att",

          "rushing attempts"

        );

        const ypc = getVal(k, "ypc");

        const ru_yds = getVal(k, "ru yards", "ru_yards", "ru_yds", "rush yards");

        const ru_td = getVal(k, "ru td", "ru_td", "rush td");

        const targets = getVal(k, "targets");

        const tgt_share = getVal(k, "tgt share", "target share", "tgt_share");

        const rec = getVal(k, "rec");

        const rec_yds = getVal(

          k,

          "rec yards",

          "rec\u00A0yards",

          "rec_yards",

          "rec_yds",

          "receiving yards",

          "rec yds"

        );

        const rec_td = getVal(k, "rec td", "rec_td");

        // DST stats

        const sacks = getVal(k, "sacks");

        const fumbles = getVal(k, "fumbles");

        const def_td = getVal(k, "deftd", "def td", "def_td");

        const points = getVal(k, "points");

        // DK/FD core

        const dk_proj = getVal(k, "dk proj", "dk_proj", "dk\u00a0proj");

        const dk_val = getVal(k, "dk val", "dk_val", "dk\u00a0val");

        const dk_pown = getVal(

          k,

          "dk pown%",

          "dk pown",

          "dk_pown",

          "dk pown %",

          "dk_pown_pct",

          "dk own%",

          "dk\u00a0own%"

        );

        const fd_proj = getVal(k, "fd proj", "fd_proj", "fd\u00a0proj");

        const fd_val = getVal(k, "fd val", "fd_val", "fd\u00a0val");

        const fd_pown = getVal(

          k,

          "fd pown%",

          "fd pown",

          "fd_pown",

          "fd pown %",

          "fd_pown_pct",

          "fd own%",

          "fd\u00a0own%"

        );

        return {

          player,

          team,

          opp,

          dk_sal,

          fd_sal,

          // QB

          pa_yards,

          pa_att,

          pa_comp,

          pa_pct,

          pa_td,

          int: intVal,

          // RB/WR/TE

          ru_att,

          ypc,

          ru_yds,

          ru_td,

          targets,

          tgt_share,

          rec,

          rec_yds,

          rec_td,

          // DST

          sacks,

          fumbles,

          def_td,

          points,

          // DK/FD

          dk_proj,

          dk_val,

          dk_pown,

          fd_proj,

          fd_val,

          fd_pown,

        };

      }),

    [rawRows, nameToTeam, nameToOpp, teamToOpp]

  );

  // search / team filter / WR rushing visibility

  const [q, setQ] = useState("");

  const [selectedTeam, setSelectedTeam] = useState("ALL");

  const [selectedGame, setSelectedGame] = useState("ALL");

  const gameOptions = useMemo(() => {

    const available = new Set(rows.map(gameKey).filter(Boolean));

    const options = new Map();

    for (const row of vegasRows) {

      const team = String(row.team || "").toUpperCase().replace(/^JAC$/, "JAX");

      const opp = String(row.opponent || "").toUpperCase().replace(/^JAC$/, "JAX");

      if (!team || !opp) continue;

      const key = [team, opp].sort().join("@");

      if (!available.has(key) || options.has(key)) continue;

      const home = String(row.field || "").toUpperCase().replace(/^JAC$/, "JAX");

      const label = home === team ? `${opp} @ ${team}` : home === opp ? `${team} @ ${opp}` : `${team} vs ${opp}`;

      options.set(key, { key, label, kickoff: String(row.gameDate || "") });

    }

    for (const key of available) {

      if (!options.has(key)) options.set(key, { key, label: key.replace("@", " vs "), kickoff: "" });

    }

    return [...options.values()].sort((a, b) => {

      if (a.kickoff && b.kickoff) return a.kickoff.localeCompare(b.kickoff) || a.label.localeCompare(b.label);

      if (a.kickoff || b.kickoff) return a.kickoff ? -1 : 1;

      return a.label.localeCompare(b.label);

    });

  }, [rows, vegasRows]);

  useEffect(() => {

    if (selectedGame !== "ALL" && !gameOptions.some((game) => game.key === selectedGame)) setSelectedGame("ALL");

  }, [gameOptions, selectedGame]);

  const [hideWrRushing, setHideWrRushing] = useState(true);

  const teams = useMemo(

    () =>

      [...new Set(rows.map((r) => String(r.team || "").trim().toUpperCase()).filter(Boolean))].sort(),

    [rows]

  );

  // If the selected team disappears when changing positions/data, reset to All Teams.

  useEffect(() => {

    if (selectedTeam !== "ALL" && !teams.includes(selectedTeam)) {

      setSelectedTeam("ALL");

    }

  }, [teams, selectedTeam]);

  const cols = useMemo(() => {

    if (activePos !== "WR" || !hideWrRushing) return baseCols;

    const wrRushingCols = new Set(["ru_att", "ypc", "ru_yds", "ru_td"]);

    return baseCols.filter((c) => !wrRushingCols.has(c.id));

  }, [activePos, hideWrRushing, baseCols]);

  const filtered = useMemo(() => {

    const n = q.trim().toLowerCase();

    return rows.filter((r) => {

      const matchesSearch =

        !n || `${r.player} ${r.team || ""} ${r.opp || ""}`.toLowerCase().includes(n);

      const matchesTeam =

        selectedTeam === "ALL" ||

        String(r.team || "").toUpperCase() === selectedTeam;

      return matchesSearch && matchesTeam && (selectedGame === "ALL" || gameKey(r) === selectedGame);

    });

  }, [rows, q, selectedTeam, selectedGame]);

  const [sort, setSort] = useState({ key: "dk_proj", dir: "desc" });

  const sorted = useMemo(() => {

    const { key, dir } = sort;

    const sgn = dir === "asc" ? 1 : -1;

    const out = [...filtered];

    out.sort((a, b) => {

      const isPct = key.endsWith("pown") || key === "pa_pct" || key === "tgt_share";

      const av = isPct ? num(String(a[key]).replace("%", "")) : num(a[key]);

      const bv = isPct ? num(String(b[key]).replace("%", "")) : num(b[key]);

      const aa = av == null ? -Infinity : av;

      const bb = bv == null ? -Infinity : bv;

      return (aa - bb) * sgn;

    });

    return out;

  }, [filtered, sort]);

  const onSort = (col) =>

    setSort((prev) =>

      prev.key === col.id

        ? { key: col.id, dir: prev.dir === "desc" ? "asc" : "desc" }

        : { key: col.id, dir: "desc" }

    );

  /* --------- heatmap stats (visible rows) -------- */

  const heatStats = useMemo(() => {

    const stats = {};

    if (!sorted.length) return stats;

    for (const c of cols) {

      const d = dirFor(c.id, activePos);

      if (!d) continue;

      let min = Infinity,

        max = -Infinity;

      for (const r of sorted) {

        const v = num(c.type === "pct" ? String(r[c.id]).replace("%", "") : r[c.id]);

        if (v == null) continue;

        if (v < min) min = v;

        if (v > max) max = v;

      }

      if (min !== Infinity && max !== -Infinity) stats[c.id] = { min, max, dir: d };

    }

    return stats;

  }, [sorted, cols, activePos]);

  // updated timestamp from meta.json

  const updatedStamp = useMemo(() => {

    const art = meta?.artifacts?.find((x) => String(x.path || "").endsWith(PLAYER_SIMULATIONS_URL.replace(/^\//, "")));

    const raw = art?.last_updated || art?.tags?.last_updated || meta?.last_updated;

    if (!raw) return null;

    const d = new Date(raw);

    if (isNaN(d.getTime())) return null;

    return d.toLocaleString(undefined, {

      month: "numeric",

      day: "numeric",

      hour: "numeric",

      minute: "2-digit",

    });

  }, [meta]);

  /* ---------- styling (mirrors NflProjections) --------- */

  const cell = "px-3 py-1 text-center";

  const headerBase = "px-3 py-1 font-semibold text-center";

  const small = "text-[12px] md:text-[13px]";

  const outerClass = isDark

    ? "min-h-screen bg-slate-950 text-slate-100"

    : "min-h-screen bg-slate-100 text-slate-900";

  const cardClass = isDark

    ? "rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm"

    : "rounded-2xl border border-slate-200 bg-white shadow-sm";

  const headerBgClass = isDark ? "bg-slate-900 text-slate-200" : "bg-slate-50 text-slate-700";

  const headerBgCell = isDark ? "bg-slate-900" : "bg-slate-50";

  const rowOddClass = isDark ? "bg-slate-950" : "bg-white";

  const rowEvenClass = isDark ? "bg-slate-900" : "bg-gray-50";

  const tableTextClass = isDark ? "text-slate-100" : "text-slate-900";

  const posBtnBase = "relative h-8 pb-2 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400";

  const posBtnOn = "text-sky-500 after:absolute after:left-0 after:right-0 after:-bottom-px after:h-0.5 after:bg-sky-400";

  const posBtnOff = isDark

    ? "text-slate-500 hover:text-slate-200"

    : "text-slate-500 hover:text-slate-800";

  return (

    <div className={outerClass}>

      <div className="w-full px-4 md:px-6 py-5">

        {/* Header: title + rows + updated + theme toggle */}

        <header className="mb-3 flex flex-wrap items-center justify-between gap-x-5 gap-y-3">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">NFL — Positional Projections</h1>
            <span className={`text-xs italic ${isDark ? "text-slate-400" : "text-slate-600"}`}>Updated: {updatedStamp ?? "—"}</span>
          </div>
            {/* theme toggle */}

            <div

              className={

                "inline-flex items-center rounded-full px-1 py-1 text-[11px] ring-1 " +

                (isDark ? "bg-slate-900/90 ring-slate-700/80" : "bg-white ring-slate-300")

              }

            >

              <button

                type="button"

                onClick={() => setTheme("dark")}

                className={

                  "px-3 py-1 rounded-full font-medium transition " +

                  (isDark ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-500 hover:text-slate-800")

                }

              >

                Dark

              </button>

              <button

                type="button"

                onClick={() => setTheme("light")}

                className={

                  "px-3 py-1 rounded-full font-medium transition " +

                  (!isDark ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-400 hover:text-slate-100")

                }

              >

                Light

              </button>

            </div>

        </header>

        <section aria-label="Slate and site" className={cardClass + " mb-3 p-3"}>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <label className="flex items-center gap-2 text-xs">

              Slate

              <select aria-label="Slate" value={slate} onChange={(event) => setSlate(event.target.value)}

                className={"h-8 min-w-0 rounded-md border px-3 " + (isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300")}>

                {SLATES.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}

              </select>

            </label>

            <label className="flex items-center gap-2 text-xs">

              Site

              <select aria-label="Slate site" value={slateSite} onChange={(event) => setSlateSite(event.target.value)}

                className={"h-8 min-w-0 rounded-md border px-3 " + (isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300")}>

                <option value="both">DK + FD</option>

                <option value="dk">DraftKings</option>

                <option value="fd">FanDuel</option>

              </select>

            </label>

          </div>
        </section>

        <section aria-label="Projections" className={cardClass}>
          <div className={"border-b p-3 " + (isDark ? "border-slate-800" : "border-slate-200")}>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h2 className="text-sm font-semibold">{activePos} Projections</h2>
                <span className={"text-xs " + (isDark ? "text-slate-400" : "text-slate-600")}>
                  {!loading && !err && <>{sorted.length.toLocaleString()} of {rawRows.length.toLocaleString()} players</>}
                </span>
              </div>
            <button

              className="h-8 shrink-0 px-3 rounded-md bg-sky-500 text-slate-950 text-xs font-semibold hover:bg-sky-400 shadow-sm"

              onClick={() =>

                downloadCSV(

                  sorted,

                  cols,

                  `nfl_${activePos.toLowerCase()}_${slate}_${slateSite}_projections.csv`

                )

              }

            >

              Export CSV

            </button>            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex shrink-0 flex-wrap items-center gap-3">
              {POS_LIST.map((p) => (

                <button

                  key={p}

                  type="button"
                  aria-pressed={activePos === p}

                  onClick={() => setActivePos(p)}

                  className={`${posBtnBase} ${activePos === p ? posBtnOn : posBtnOff}`}

                  title={`Show ${p}`}

                >

                  {p}

                </button>

              ))}

              </div>


            <label className="flex items-center gap-2 text-xs">

              Game

              <select aria-label="Game" value={selectedGame} onChange={(event) => setSelectedGame(event.target.value)}

                className={"h-8 min-w-0 rounded-md border px-3 text-xs " + (isDark ? "bg-slate-950/80 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-900")}>

                <option value="ALL">All Games</option>

                {gameOptions.map((game) => <option key={game.key} value={game.key}>{game.label}</option>)}

              </select>

            </label>

            <select

              value={selectedTeam}

              onChange={(e) => setSelectedTeam(e.target.value)}

              className={

                "h-8 min-w-0 rounded-md border px-3 text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 " +

                (isDark

                  ? "bg-slate-950/80 border-slate-700 text-slate-100"

                  : "bg-white border-slate-300 text-slate-900")

              }

              aria-label="Team"
              title="Filter by team"

            >

              <option value="ALL">All Teams</option>

              {teams.map((team) => (

                <option key={team} value={team}>

                  {team}

                </option>

              ))}

            </select>

            {activePos === "WR" && (

              <label

                className={

                  "inline-flex h-8 items-center gap-2 rounded-md border px-3 text-xs cursor-pointer select-none " +

                  (isDark

                    ? "bg-slate-950/80 border-slate-700 text-slate-200"

                    : "bg-white border-slate-300 text-slate-700")

                }

              >

                <input

                  type="checkbox"

                  checked={hideWrRushing}

                  onChange={(e) => setHideWrRushing(e.target.checked)}

                  className="h-4 w-4 accent-sky-500"

                />

                <span>Hide WR rushing stats</span>

              </label>

            )}



            <input

              className={

                "h-8 w-full sm:ml-auto sm:w-64 rounded-md border px-3 text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 " +

                (isDark

                  ? "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500"

                  : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400")

              }

              placeholder="Search player / team / opp…"

              value={q}

              onChange={(e) => setQ(e.target.value)}

            />
            </div>
          </div>

          {/* Inner scroller */}

          <div className="overflow-auto h-[70vh] rounded-b-2xl overscroll-contain">

            <table className={`min-w-max w-full border-separate ${small} ${tableTextClass}`} style={{ borderSpacing: 0 }}>

              <thead

                className={`${headerBgClass} sticky top-0 z-20 shadow-[0_2px_0_0_rgba(0,0,0,0.35)]`}

              >

                <tr>

                  {cols.map((c) => (

                    <th

                      key={c.id}

                      className={`${headerBase} whitespace-nowrap cursor-pointer select-none ${c.w || ""} ${headerBgCell} ${

                        c.id === "player" ? "sticky left-0 z-30" : ""

                      }`}

                      title="Click to sort"

                      onClick={() => onSort(c)}

                    >

                      <div className="inline-flex items-center gap-1">

                        <span>{c.label}</span>

                        <span className="text-gray-400">

                          {sort.key === c.id ? (sort.dir === "desc" ? "▼" : "▲") : "▲"}

                        </span>

                      </div>

                    </th>

                  ))}

                </tr>

              </thead>

              <tbody>

                {loading && (

                  <tr>

                    <td className={`${cell} text-slate-400`} colSpan={cols.length}>

                      Loading…

                    </td>

                  </tr>

                )}

                {err && (

                  <tr>

                    <td className={`${cell} text-red-400`} colSpan={cols.length}>

                      Failed to load: {err}

                    </td>

                  </tr>

                )}

                {!loading &&

                  !err &&

                  sorted.map((r, i) => {

                    const rowBg = i % 2 === 0 ? rowOddClass : rowEvenClass;

                    return (

                      <tr key={`${r.player || i}-${i}`} className={rowBg}>

                        {cols.map((c) => {

                          if (c.id === "player") {

                            const val = r[c.id] ?? "";

                            return (

                              <td

                                key={c.id}

                                className={`px-3 py-1 text-left sticky left-0 z-10 ${rowBg} shadow-[inset_-6px_0_8px_-6px_rgba(0,0,0,0.6)] whitespace-nowrap`}

                                title={String(val)}

                              >

                                <div className="flex items-center gap-2">

                                  {/* Only show logo when we have a real team abbrev */}

                                  {r.team ? (

                                    <img

                                      src={teamLogo(r.team)}

                                      alt=""

                                      className="h-4 w-4 object-contain rounded-sm"

                                      onError={(e) => (e.currentTarget.style.visibility = "hidden")}

                                    />

                                  ) : null}

                                  <span>{val}</span>

                                </div>

                              </td>

                            );

                          }

                          if (c.type === "team") {

                            const abbr = String(r[c.id] || "").toUpperCase();

                            return (

                              <td

                                key={c.id}

                                className={`px-3 py-1 text-center whitespace-nowrap`}

                                title={abbr}

                              >

                                <div className="inline-flex items-center gap-2 justify-center">

                                  {abbr && (

                                    <img

                                      src={teamLogo(abbr)}

                                      alt={abbr}

                                      className="h-4 w-4 object-contain"

                                    />

                                  )}

                                  <span>{abbr}</span>

                                </div>

                              </td>

                            );

                          }

                          const stat = heatStats[c.id];

                          const vNum = num(

                            c.type === "pct" ? String(r[c.id]).replace("%", "") : r[c.id]

                          );

                          const textColor = stat

                            ? heatTextColor(stat.min, stat.max, vNum, stat.dir, theme)

                            : null;

                          let val = r[c.id] ?? "";

                          if (c.type === "money") val = int0(val);

                          else if (c.type === "pct") val = pct(val);

                          else if (c.type === "num1") val = smart1(val);

                          return (

                          <td

                            key={c.id}

                            className={`${cell} tabular-nums whitespace-nowrap`}

                            title={String(val)}

                            style={textColor ? { color: textColor, fontWeight: 600 } : undefined}

                          >

                            {val}

                          </td>

                          );

                        })}

                      </tr>

                    );

                  })}

              </tbody>

            </table>

          </div>

        </section>

      </div>

    </div>

  );

}
